// AddWellModal.js
// Lets the user pick one or more wells from the sampling site catalog and
// attach them to one of the event's existing sampling groups. Used to fix
// up an active sampling event that was created without one of its wells.

import React, { useState, useEffect, useMemo } from "react";
import styles from "../DailySummaryTab.module.css";
import { getSamplingSiteInfo } from "../../../../../api/SamplingEventsApi";

export const AddWellModal = ({
  isOpen,
  onClose,
  onSubmit,
  samplingEventData,
}) => {
  const [siteWells, setSiteWells] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedWellIds, setSelectedWellIds] = useState([]);
  const [samplingGroupId, setSamplingGroupId] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const existingWellIds = useMemo(() => {
    const ids = new Set();
    (samplingEventData?.samplingEventWells || []).forEach((w) => {
      if (w?.wellId) ids.add(w.wellId);
    });
    return ids;
  }, [samplingEventData?.samplingEventWells]);

  const samplingGroups = samplingEventData?.samplingGroups || [];

  useEffect(() => {
    if (!isOpen) return;
    setSelectedWellIds([]);
    setError("");
    setSubmitting(false);
    setSamplingGroupId(
      samplingGroups.length === 1 ? samplingGroups[0].id : ""
    );

    const siteName = samplingEventData?.wellSite;
    if (!siteName) {
      setSiteWells([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    getSamplingSiteInfo(siteName, true)
      .then((response) => {
        if (cancelled) return;
        const wells = response?.data?.wells || [];
        setSiteWells(wells);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load sampling site wells:", err);
        setError("Unable to load wells for this site. Please try again.");
        setSiteWells([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, samplingEventData?.wellSite]);

  const availableWells = useMemo(() => {
    return siteWells
      .filter((w) => !existingWellIds.has(w.well_id))
      .sort((a, b) =>
        String(a.well_id || "").localeCompare(String(b.well_id || ""), undefined, {
          numeric: true,
        })
      );
  }, [siteWells, existingWellIds]);

  const toggleWell = (wellId) => {
    setSelectedWellIds((prev) =>
      prev.includes(wellId)
        ? prev.filter((id) => id !== wellId)
        : [...prev, wellId]
    );
    if (error) setError("");
  };

  const handleSubmit = async () => {
    if (!samplingGroupId) {
      setError("Please select a sampling group.");
      return;
    }
    if (selectedWellIds.length === 0) {
      setError("Please select at least one well to add.");
      return;
    }

    const wellsToAdd = availableWells.filter((w) =>
      selectedWellIds.includes(w.well_id)
    );

    setSubmitting(true);
    try {
      await onSubmit({ wells: wellsToAdd, samplingGroupId });
      onClose();
    } catch (err) {
      console.error("Error adding wells:", err);
      setError("Failed to add the selected wells. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>Add Well to Sampling Event</h3>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>
        <div className={styles.modalBody}>
          <div style={{ marginBottom: "1rem" }}>
            <label htmlFor="addWellGroupSelect">
              <strong>Sampling Group:</strong>
            </label>
            <select
              id="addWellGroupSelect"
              value={samplingGroupId}
              onChange={(e) => {
                setSamplingGroupId(e.target.value);
                if (error) setError("");
              }}
              style={{
                width: "100%",
                padding: "0.5rem",
                marginTop: "0.25rem",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            >
              <option value="">-- Select a group --</option>
              {samplingGroups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.groupName || "(unnamed group)"}
                </option>
              ))}
            </select>
          </div>

          <div>
            <strong>Available Wells</strong>
            <p style={{ fontSize: "0.85rem", color: "#666", margin: "0.25rem 0 0.5rem" }}>
              Wells already on this event are filtered out.
            </p>
            {loading ? (
              <div className={styles.noData}>Loading wells...</div>
            ) : availableWells.length === 0 ? (
              <div className={styles.noData}>
                No additional wells available for {samplingEventData?.wellSite || "this site"}.
              </div>
            ) : (
              <div
                style={{
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  maxHeight: "240px",
                  overflowY: "auto",
                  padding: "0.5rem",
                }}
              >
                {availableWells.map((well) => {
                  const isChecked = selectedWellIds.includes(well.well_id);
                  return (
                    <label
                      key={well.well_id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "0.35rem 0.25rem",
                        cursor: "pointer",
                        borderBottom: "1px solid #f0f0f0",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleWell(well.well_id)}
                        style={{ marginRight: "0.5rem" }}
                      />
                      <span>
                        <strong>{well.well_id}</strong>
                        {well.well_purpose ? ` — ${well.well_purpose}` : ""}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {error && (
            <p style={{ color: "var(--error-color, #c33)", marginTop: "0.75rem" }}>
              {error}
            </p>
          )}
        </div>
        <div className={styles.modalActions}>
          <button
            className={styles.cancelButton}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className={styles.confirmButton}
            onClick={handleSubmit}
            disabled={
              submitting ||
              loading ||
              selectedWellIds.length === 0 ||
              !samplingGroupId
            }
          >
            {submitting
              ? "Adding..."
              : `Add ${selectedWellIds.length || ""} Well${
                  selectedWellIds.length === 1 ? "" : "s"
                }`}
          </button>
        </div>
      </div>
    </div>
  );
};
